import React from "react";
import { NavLink } from "react-router-dom";
import { useInfiniteQuery, useQueryClient, useQuery } from "react-query";
import { m, LazyMotion, domAnimation, useAnimation } from "framer-motion";
import { isBrowser } from "react-device-detect";
import {
    LoadingDots,
    FragmentCard,
    SearchField,
    Button,
    Spinner,
    Dropdown,
    ClipsOnboarding,
    LoginOnboarding,
    Icon,
    HamburgerMenu,
} from "ui/components";
import { useSessionContext } from "ui/context/SessionContext";
import {
    listFragments,
    deleteFragment,
    createCompilation,
} from "core/services/site";
import { Fragment, DropdownOption, Channel, EditOp } from "core/models";
import { useNotifications } from "ui/context/NotificationsContext";
import { useTaskContext } from "ui/context/TaskContext";
import { listChannels, listUsersInProject } from "core/services";
import { User } from "core/models/User";
import { cx } from "utils/join-classnames";
import UploadManager from "core/services/uploadManager";
import { createFragmentFromFile } from "core/services/site/fragment";
import usePermissions from "ui/hooks/usePermissions";
import aspectRatioOptions from "core/constants/edits";
import useQueue from "ui/hooks/useQueue";
import styles from "./Clips.module.scss";
import pwa_styles from "./Clips_Pwa.module.scss";

const instanceOfEdit = (data: any): data is EditOp => "cropped" in data;
const isAddon = (props: EditOp) =>
    props.introkey || props.outrokey || props.overlaykey;

const fragmentHasAddon = (fragment: Fragment): boolean =>
    fragment.properties.filter(
        (prop) =>
            prop.key === "spoton-editop" &&
            instanceOfEdit(prop.value) &&
            isAddon(prop.value),
    ).length > 0;

const Clips: React.FC = () => {
    const sessionContext = useSessionContext();
    const queryClient = useQueryClient();
    const taskContext = useTaskContext();
    const userPermissions = usePermissions();
    const queue = useQueue();
    const { addNotification, updateNotification } = useNotifications();

    const hiddenFileInput = React.useRef<HTMLInputElement>(
        null,
    ) as React.MutableRefObject<HTMLInputElement>;
    const scrollPush = React.useRef<HTMLDivElement>(
        null,
    ) as React.MutableRefObject<HTMLDivElement>;

    const [fragmentsEnded, setFragmentsEnded] = React.useState<boolean>(false);
    const [pending, setPending] = React.useState<boolean>(false);
    const [continuationToken, setContinuationToken] = React.useState<string>();
    const [channelOptions, setChannelOptions] = React.useState<
        DropdownOption[]
    >([]);
    const [aspectOptions, setAspectOptions] = React.useState<DropdownOption[]>(
        [],
    );
    const [initialUsers, setInitialUsers] = React.useState<DropdownOption[]>(
        [],
    );
    const [creators, setCreators] = React.useState<DropdownOption[]>([]);

    const [compilationMode, setCompilationMode] =
        React.useState<boolean>(false);
    const [compilationSelection, setCompilationSelection] = React.useState<
        number[]
    >([]);
    const [compilationDuration, setCompilationDuration] =
        React.useState<number>(0);

    const [channelFilter, setChannelFilter] = React.useState<
        number | undefined
    >(undefined);
    const [ratioFilter, setRatioFilter] = React.useState<string | undefined>(
        undefined,
    );
    const [userFilter, setUserFilter] = React.useState<number>();
    const [searchContinuationToken, setSearchContinuationToken] =
        React.useState<string>();
    const [searching, setSearching] = React.useState<boolean>(false);
    const [searchQuery, setSearchQuery] = React.useState<string>();
    const [searchResults, setSearchResults] = React.useState<Fragment[]>([]);
    const [searchPending, setSearchPending] = React.useState<boolean>(false);
    const [pushVisible, setPushVisible] = React.useState<boolean>(false);
    const [collapsedOptions, setCollapsedOptions] =
        React.useState<boolean>(false);

    const [selectedUser, setSelectedUser] = React.useState<string>();
    const [usersPending, setUsersPending] = React.useState<boolean>(false);

    // Permission checks
    const userCanUpload = userPermissions.canUploadFragment;
    const userCanCreateCompilation = userPermissions.canCreateCompilation;

    // Scroll Push Observer
    const observer = new IntersectionObserver(
        async (entries) => {
            setPushVisible(entries[0].isIntersecting);
        },
        {
            root: document.querySelector(".source-js-app--App-module__main"),
            rootMargin: "0px 0px 500px 0px",
            threshold: 0,
        },
    );
    React.useEffect(() => {
        if (scrollPush.current) {
            observer.observe(scrollPush.current);
        }
        return () => {
            observer.disconnect();
        };
    }, []);

    const getFragments = async ({
        pageParam = undefined,
    }): Promise<Fragment[]> => {
        if (!sessionContext.session?.token || !sessionContext.selectedProject) {
            throw Error("Missing data for getFragments");
        }
        const fragmentResult = await listFragments({
            accessToken: sessionContext.session.token,
            projectId: sessionContext.selectedProject.id,
            continuationToken: pageParam,
        });
        if (fragmentResult.error || !fragmentResult.value) {
            throw Error(
                fragmentResult.error || "error during listFragments call",
            );
        }
        if (fragmentResult.value.fragments.length === 0) {
            setFragmentsEnded(true);
        }
        setContinuationToken(fragmentResult.value.continuationToken);
        taskContext.update();
        return fragmentResult.value.fragments;
    };

    const {
        data: clips,
        status,
        fetchNextPage,
    } = useInfiniteQuery("fragments", getFragments, {});

    const loadMoreSearchResults = async () => {
        if (
            !sessionContext.selectedProject?.id ||
            !sessionContext.session ||
            !searchContinuationToken ||
            pending
        ) {
            return;
        }
        const { session } = sessionContext;
        setPending(true);
        const searchResult = await listFragments({
            accessToken: session.token,
            projectId: sessionContext.selectedProject.id,
            continuationToken: searchContinuationToken,
        });
        if (searchResult.error || !searchResult.value) {
            setPending(false);
            return;
        }
        setSearchContinuationToken(searchResult.value.continuationToken);
        let newSearchResults = [...searchResults];
        newSearchResults = newSearchResults.concat(
            searchResult.value.fragments,
        );
        setSearchResults(newSearchResults);
        setPending(false);
    };
    React.useEffect(() => {
        if (pushVisible && continuationToken && !pending && !searching) {
            fetchNextPage({ pageParam: continuationToken });
        }
        if (pushVisible && searchContinuationToken && !pending && searching) {
            loadMoreSearchResults();
        }
    }, [pushVisible]);

    const handleDelete = async (fragmentID: number) => {
        if (!sessionContext.session) {
            return;
        }
        setCompilationSelection([]);
        await deleteFragment(sessionContext.session.token, fragmentID);
        queryClient.invalidateQueries("fragments");
    };

    const selectCompilationClip = (fragmentId: number) => {
        let newSelection = [...compilationSelection];
        if (newSelection.some((item) => item === fragmentId)) {
            // Check if fragmentId is already in selection, delete if so.
            newSelection = newSelection.filter((item) => item !== fragmentId);
            setCompilationSelection(newSelection);
        } else {
            setCompilationSelection(newSelection.concat([fragmentId]));
        }
    };

    React.useEffect(() => {
        if (taskContext.finishedTasks.length === 0) {
            return;
        }
        queryClient.invalidateQueries("fragments");
    }, [taskContext.finishedTasks]);

    const onEdit = React.useCallback(
        (
            fragment: Fragment,
            notificationId: string,
            successText: string,
        ): void => {
            queryClient.invalidateQueries("fragments");
            taskContext.newTask(
                fragment.processingTaskId,
                notificationId,
                successText,
            );
        },
        [queryClient, taskContext],
    );

    const compilationCreation = async () => {
        if (compilationSelection.length === 0) {
            return;
        }
        const compilationNotification = addNotification(
            "normal",
            "Creating compilation",
            "compiling your selection of clips to one file",
            0,
        );
        setCompilationMode(false);
        try {
            if (!sessionContext.session) {
                throw Error("no sessoin");
            }
            const result = await createCompilation(
                sessionContext.session.token,
                compilationSelection,
            );
            if (result.error || !result.value) {
                throw new Error("error in createCompilation result");
            }
            setCompilationSelection([]);
            onEdit(
                result.value,
                compilationNotification.id,
                "Your compilation is ready",
            );
            setRatioFilter(undefined);
        } catch (err) {
            updateNotification(
                compilationNotification.id,
                "error",
                "something went wrong",
                " creating your compilation",
                10000,
            );
        }
    };

    const getChannels = async (): Promise<Channel[]> => {
        if (!sessionContext.session || !sessionContext.selectedProject) {
            throw Error("Missing data for getChannels");
        }
        const channelResponse = await listChannels(
            sessionContext.session.token,
            sessionContext.selectedProject.id,
        );
        if (channelResponse.error || !channelResponse.value) {
            throw Error(
                channelResponse.error || "Error during listChannels call",
            );
        }
        return channelResponse.value;
    };

    const { data: channels } = useQuery("channels", getChannels);

    React.useEffect(() => {
        if (!channels) {
            return;
        }
        const newChannelOptions: DropdownOption[] =
            channels.map((channel) => ({
                id: channel.id,
                title: channel.name,
                value: channel.id.toString(),
            })) || [];
        newChannelOptions.unshift({
            id: 0,
            title: "All  Channels",
            value: undefined,
        });
        setChannelOptions(newChannelOptions);
    }, [channels]);

    React.useEffect(() => {
        const newAspectOptions: DropdownOption[] = [
            {
                id: 0,
                title: "All aspectratio",
                value: undefined,
            },
            ...aspectRatioOptions,
        ];
        setAspectOptions(newAspectOptions);
        getChannels();
        taskContext.update();
    }, []);

    React.useEffect(() => {
        let totalDuration = 0;
        const fragments =
            clips?.pages.reduce((total, page) => total.concat(page), []) || [];

        compilationSelection.forEach((item) => {
            const theFragment = fragments.find(
                (fragment) => fragment.id === item,
            );
            if (!theFragment) {
                return;
            }
            totalDuration += theFragment.getDuration();
        });
        setCompilationDuration(Math.round(totalDuration));
    }, [compilationSelection]);

    const getSearchResults = React.useCallback(
        async (query?: string) => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                if (!sessionContext.selectedProject) {
                    throw Error("no selected project");
                }
                setSearchPending(true);
                const searchResult = await listFragments({
                    accessToken: sessionContext.session.token,
                    projectId: sessionContext.selectedProject.id,
                    searchText: query,
                    channelId: channelFilter,
                    aspectRatio: ratioFilter,
                    userId: userFilter,
                });
                if (searchResult.error) {
                    return;
                }
                setSearchContinuationToken(
                    searchResult.value?.continuationToken,
                );
                if (searchResult.value) {
                    setSearchResults(searchResult.value.fragments);
                }
                setSearchPending(false);
            } catch (err) {
                addNotification(
                    "error",
                    "Error getting search results",
                    "please try again",
                    5000,
                );
                setSearchPending(false);
            }
        },
        [
            addNotification,
            channelFilter,
            ratioFilter,
            userFilter,
            sessionContext.selectedProject?.id,
            sessionContext.session?.token,
        ],
    );

    const fetchUsers = React.useCallback(
        async (query?: string, uuid?: string): Promise<DropdownOption[]> => {
            try {
                if (!sessionContext.session) {
                    throw Error("no session");
                }
                if (!sessionContext.selectedProject?.id) {
                    throw Error("no projectId");
                }

                const fetchedUsers = await listUsersInProject({
                    accessToken: sessionContext.session.token,
                    usernameLike: query,
                    projectId: sessionContext.selectedProject.id,
                    uuid,
                });
                if (fetchedUsers.error || !fetchedUsers.value) {
                    throw new Error("Error while fetching users");
                }
                const getUsername = (user: User): string | undefined => {
                    let { name } = user;
                    if (!name || name.length === 0) {
                        name = user.username;
                    }
                    return name;
                };
                const dropdownUsers = fetchedUsers.value.map((user) => {
                    const username = getUsername(user);
                    return {
                        id: user.id,
                        title: `${username}`,
                        value: user.id.toString(),
                    };
                });
                return dropdownUsers;
            } catch (err) {
                addNotification(
                    "error",
                    "Error while fetching users",
                    (err as Error).message,
                    5000,
                );
                return [];
            }
        },
        [sessionContext.session?.token],
    );

    const getInitialUsers = async () => {
        let requestPending = true;
        setUsersPending(true);
        const dropdownUsers = await fetchUsers(
            "",
            selectedUser?.split(":")[1] || undefined,
        );
        if (requestPending) {
            setCreators(dropdownUsers);
            setInitialUsers(dropdownUsers);
            setUsersPending(false);
        }
        return () => {
            requestPending = false;
        };
    };

    const searchUsers = React.useCallback(
        async (query: string) => {
            setUsersPending(true);
            if (query.length < 2) {
                setCreators(initialUsers);
                setUsersPending(false);
                return;
            }
            const dropdownUsers = await fetchUsers(query);
            setUsersPending(false);
            setCreators(dropdownUsers);
        },
        [initialUsers, fetchUsers],
    );

    const onSearch = React.useCallback(
        async (query: string) => {
            setSearchQuery(query);
            if (query === "" && !channelFilter && !ratioFilter && !userFilter) {
                setSearching(false);
            } else {
                await getSearchResults(query);
                setSearching(true);
            }
        },
        [channelFilter, getSearchResults, ratioFilter, userFilter],
    );

    React.useEffect(() => {
        if (channelFilter || ratioFilter || userFilter) {
            setSearching(true);
            getSearchResults(searchQuery);
            getInitialUsers();
        } else if (searchQuery && searchQuery !== "") {
            getSearchResults(searchQuery);
        } else {
            setSearching(false);
        }
    }, [channelFilter, ratioFilter, userFilter]);

    const handleUploadClick = () => {
        hiddenFileInput.current.click();
    };

    const handleUpload = async (fileUploaded: File) => {
        const uploadNotification = addNotification(
            "normal",
            "Uploading fragment (0%)",
            "this can take a minute",
            0,
        );
        try {
            if (!sessionContext.session) {
                throw Error("no session");
            }
            if (!sessionContext.selectedProject) {
                throw Error("no selected project");
            }
            const uploadManager = new UploadManager(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
            );
            const uploadedFragment = await uploadManager.uploadFragment(
                fileUploaded,
                (progress) => {
                    updateNotification(
                        uploadNotification.id,
                        "normal",
                        `Uploading fragment (${Math.ceil(progress * 100)}%)`,
                        "This can take a minute",
                        0,
                    );
                },
            );
            if (uploadedFragment.error || !uploadedFragment.value) {
                throw Error("problem uploading fragment");
            }
            const createdFragment = await createFragmentFromFile(
                sessionContext.session.token,
                sessionContext.selectedProject.id,
                fileUploaded.name,
                uploadedFragment.value.id,
            );
            if (createdFragment.error || !createdFragment.value) {
                throw Error("problem while creating fragment");
            }
            updateNotification(
                uploadNotification.id,
                "normal",
                `Creating fragment`,
                "Uploading done",
                0,
            );
            onEdit(
                createdFragment.value,
                uploadNotification.id,
                "Fragment done",
            );
        } catch (err) {
            updateNotification(
                uploadNotification.id,
                "error",
                `Error uploading fragment`,
                (err as Error).message,
                0,
            );
        }
    };
    const initiateUploads = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!event.target.files) {
            return;
        }
        const filesArr: File[] = Array.from(event.target.files);
        filesArr.forEach((file) => {
            if (file.type.startsWith("video/")) {
                queue.enqueue(() => handleUpload(file));
            } else {
                addNotification(
                    "error",
                    "File should be a video",
                    `${file.name} had incorrect type`,
                    3000,
                );
            }
        });
    };

    const funcButtons = (
        <div className={styles.toprightnav}>
            <Button
                type="secondary"
                icon="compilation"
                onClick={() => setCompilationMode(!compilationMode)}
                active={compilationMode}
                size="large"
                disabled={!userCanCreateCompilation}
                tooltip={
                    userCanUpload
                        ? ""
                        : "Compilation is not available within your current plan"
                }
            >
                Compilation
            </Button>
            <Button
                type="secondary"
                icon="upload"
                size="large"
                onClick={handleUploadClick}
                disabled={!userCanUpload}
                tooltip={
                    userCanUpload
                        ? ""
                        : "Upload is not available within your current plan"
                }
            >
                Upload
            </Button>
            <input
                type="file"
                accept="video/*"
                ref={hiddenFileInput}
                onChange={initiateUploads}
                style={{ display: "none" }}
                multiple
            />
        </div>
    );

    const pageTitle = () => {
        if (searching && searchQuery) {
            return `Showing clips for: ${searchQuery}`;
        }
        if (compilationMode) {
            const seconds = compilationDuration % 60;
            const minutes = Math.floor(compilationDuration / 60);

            return (
                <>
                    Creating a compilation
                    <div className={styles.compilationstatus}>
                        {minutes !== 0 && `${minutes}m`} {seconds}s selected (
                        {compilationSelection.length} clips)
                    </div>
                </>
            );
        }
        return "Clips";
    };

    const memorizedOnEdit = React.useCallback(onEdit, [clips, onEdit]);
    const memorizedOnSelect = React.useCallback(selectCompilationClip, [
        compilationSelection,
    ]);
    const memorizedOnDelete = React.useCallback(handleDelete, [
        clips,
        sessionContext.session?.token,
    ]);
    const handleChangeChannelFilter = React.useCallback((value: string) => {
        if (value) {
            setChannelFilter(+value);
        } else {
            setChannelFilter(undefined);
        }
    }, []);

    const handleChangeUserFilter = React.useCallback(
        (value: string) => {
            if (value) {
                setUsersPending(true);
                setUserFilter(+value);
                setSelectedUser(value);
            } else {
                setSelectedUser(undefined);
                setUserFilter(undefined);
                setUsersPending(false);
            }
        },
        [userFilter, selectedUser, initialUsers],
    );

    const handleClipUpdate = (): void => {
        queryClient.invalidateQueries("fragments");
    };

    const controls = useAnimation();

    React.useEffect(() => {
        if (searching) {
            return;
        }
        controls.start((i) => ({
            opacity: 1,
            y: 0,
            transition: { delay: i * 0.01 },
        }));
    }, [clips, searching, controls, compilationMode]);

    if (!isBrowser) {
        return (
            <div>
                <div className={pwa_styles.header}>
                    <HamburgerMenu />
                </div>
                <div className={pwa_styles.search}>
                    {/* <div className={styles.spinner}>
                        {searchPending && <Spinner />}
                    </div> */}
                    <div className={cx(styles.optionbar__item__search)}>
                        <SearchField theme="light" onSubmit={onSearch} />
                    </div>
                </div>
                <div>
                    <LazyMotion features={domAnimation} strict>
                        <div className={pwa_styles.fragmentsgrid}>
                            {!searching &&
                                clips &&
                                clips.pages.map((group) =>
                                    group.map(
                                        (
                                            fragment,
                                            index,
                                        ): React.ReactNode | null => {
                                            if (
                                                compilationMode &&
                                                (fragmentHasAddon(fragment) ||
                                                    !fragment.isAvailable())
                                            ) {
                                                return null;
                                            }
                                            return (
                                                <m.div
                                                    initial={{
                                                        y: 100,
                                                        opacity: 0,
                                                    }}
                                                    animate={controls}
                                                    custom={index}
                                                    key={fragment.getUniqueIdentifier()}
                                                >
                                                    <FragmentCard
                                                        fragment={fragment}
                                                        selectable={
                                                            compilationMode
                                                        }
                                                        onSelect={
                                                            memorizedOnSelect
                                                        }
                                                        selectedNumber={
                                                            compilationSelection.findIndex(
                                                                (item) =>
                                                                    item ===
                                                                    fragment.id,
                                                            ) + 1
                                                        }
                                                        onDelete={
                                                            memorizedOnDelete
                                                        }
                                                        onEdit={memorizedOnEdit}
                                                        onUpdate={
                                                            handleClipUpdate
                                                        }
                                                    />
                                                </m.div>
                                            );
                                        },
                                    ),
                                )}
                            {searching &&
                                searchResults.length > 0 &&
                                searchResults.map((fragment) => (
                                    <FragmentCard
                                        key={fragment.getUniqueIdentifier()}
                                        fragment={fragment}
                                        selectable={compilationMode}
                                        onSelect={memorizedOnSelect}
                                        selectedNumber={
                                            compilationSelection.findIndex(
                                                (item) => item === fragment.id,
                                            ) + 1
                                        }
                                        onDelete={memorizedOnDelete}
                                        onEdit={memorizedOnEdit}
                                        onUpdate={handleClipUpdate}
                                    />
                                ))}
                        </div>
                        <div
                            role="presentation"
                            className={pwa_styles.add_btn}
                            onClick={handleUploadClick}
                        >
                            <Icon
                                className={pwa_styles.add_btn__icon}
                                name="plus"
                            />
                        </div>

                        <input
                            type="file"
                            accept="video/*"
                            ref={hiddenFileInput}
                            onChange={initiateUploads}
                            style={{ display: "none" }}
                            multiple
                        />
                    </LazyMotion>
                    {searching &&
                        !searchPending &&
                        searchResults.length <= 0 && (
                            <div className={styles.error}>
                                No Search results{" "}
                                {searchQuery &&
                                    searchQuery !== "" &&
                                    `for '${searchQuery}'`}
                            </div>
                        )}
                </div>
                <div className={styles.scroll__push} ref={scrollPush}>
                    <div className={styles.scroll__push__text}>
                        {(() => {
                            if (status === "error") {
                                return (
                                    <div className={styles.error}>
                                        Error while getting clips
                                    </div>
                                );
                            }
                            if (status === "loading") {
                                return (
                                    <>
                                        Loading clips
                                        <LoadingDots />
                                    </>
                                );
                            }
                            if (
                                status === "success" &&
                                clips.pages[0].length === 0
                            ) {
                                return (
                                    <div>
                                        <div
                                            className={
                                                styles.scroll__push__text
                                            }
                                        >
                                            There are no videos here yet!
                                        </div>
                                        <div
                                            className={
                                                styles.scroll__push__text
                                            }
                                        >
                                            You should create one!
                                        </div>
                                        <div className={pwa_styles.container}>
                                            <div
                                                role="presentation"
                                                className={pwa_styles.add_btn}
                                                onClick={handleUploadClick}
                                            >
                                                <Icon
                                                    className={
                                                        pwa_styles.add_btn__icon
                                                    }
                                                    name="plus"
                                                />
                                            </div>
                                        </div>

                                        <input
                                            type="file"
                                            accept="video/*"
                                            ref={hiddenFileInput}
                                            onChange={initiateUploads}
                                            style={{ display: "none" }}
                                            multiple
                                        />
                                    </div>
                                );
                            }
                            if (fragmentsEnded) {
                                return "No more clips available";
                            }
                            return null;
                        })()}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div>
            <header className={styles.heading}>
                <div className={styles.heading__titles}>
                    <h1 className={styles.heading__title}>{pageTitle()}</h1>
                    {compilationMode && (
                        <div className={styles.heading__sub}>
                            Select clips in the order you wish to merge them
                            together as 1
                        </div>
                    )}
                </div>
                <div className={styles.heading__push} />
                {funcButtons && (
                    <div className={styles.heading__content}>{funcButtons}</div>
                )}
            </header>
            <div className={styles.optionbar} role="presentation">
                <div className={styles.spinner}>
                    {searchPending && <Spinner />}
                </div>
                <div className={cx(styles.optionbar__item__search)}>
                    <SearchField theme="light" onSubmit={onSearch} />
                </div>
                <div className={styles.optionbar__item__button}>
                    <Button
                        type="secondary"
                        onClick={() => {
                            setCollapsedOptions(!collapsedOptions);
                            getInitialUsers();
                        }}
                    >
                        <Icon name={collapsedOptions ? "close" : "filter"} />
                    </Button>
                </div>
            </div>

            <div
                className={cx(collapsedOptions ? null : styles.optionbar__item)}
            >
                <div className={styles.optionbar__item__active}>
                    <div className={styles.optionbar__item__label}>
                        <p className={styles.optionbar__item__label__text}>
                            Channel
                        </p>
                        <Dropdown
                            theme="dark"
                            options={channelOptions}
                            onValueChanged={handleChangeChannelFilter}
                            placeholderText="All Channels"
                        />
                    </div>
                    <div className={styles.optionbar__item__label}>
                        <p className={styles.optionbar__item__label__text}>
                            Aspect Ratio
                        </p>
                        <Dropdown
                            theme="dark"
                            options={aspectOptions}
                            onValueChanged={setRatioFilter}
                            placeholderText="All aspect ratio"
                        />
                    </div>
                    <div className={styles.optionbar__item__label}>
                        <p className={styles.optionbar__item__label__text}>
                            Created By
                        </p>
                        <Dropdown
                            theme="dark"
                            onValueChanged={handleChangeUserFilter}
                            value={selectedUser || ""}
                            options={creators || initialUsers}
                            searchable
                            onSearch={searchUsers}
                            pending={usersPending}
                            placeholderText="Search user"
                            className={styles.optionbar__item__label__dropdown}
                        />
                    </div>
                </div>
            </div>
            {compilationMode && (
                <div className={styles.optionbar__compilation__container}>
                    <div
                        className={cx(
                            styles.optionbar__compilation,
                            styles.optionbar__item__button,
                        )}
                    >
                        <Button
                            type="secondary"
                            theme="light"
                            onClick={() => {
                                setCompilationMode(false);
                                setCompilationDuration(0);
                                setCompilationSelection([]);
                            }}
                        >
                            Cancel
                        </Button>
                    </div>
                    <div
                        className={cx(
                            styles.optionbar__compilation,
                            styles.optionbar__item__button,
                        )}
                    >
                        <Button
                            type="primary"
                            onClick={compilationCreation}
                            disabled={compilationSelection.length <= 1}
                        >
                            Create
                        </Button>
                    </div>
                </div>
            )}

            <LazyMotion features={domAnimation} strict>
                <div className={styles.fragmentsgrid}>
                    {!searching &&
                        clips &&
                        clips.pages.map((group) =>
                            group.map(
                                (fragment, index): React.ReactNode | null => {
                                    if (
                                        compilationMode &&
                                        (fragmentHasAddon(fragment) ||
                                            !fragment.isAvailable())
                                    ) {
                                        return null;
                                    }
                                    return (
                                        <m.div
                                            initial={{ y: 100, opacity: 0 }}
                                            animate={controls}
                                            custom={index}
                                            key={fragment.getUniqueIdentifier()}
                                        >
                                            <FragmentCard
                                                fragment={fragment}
                                                selectable={compilationMode}
                                                onSelect={memorizedOnSelect}
                                                selectedNumber={
                                                    compilationSelection.findIndex(
                                                        (item) =>
                                                            item ===
                                                            fragment.id,
                                                    ) + 1
                                                }
                                                onDelete={memorizedOnDelete}
                                                onEdit={memorizedOnEdit}
                                                onUpdate={handleClipUpdate}
                                            />
                                        </m.div>
                                    );
                                },
                            ),
                        )}
                    {searching &&
                        searchResults.length > 0 &&
                        searchResults.map((fragment) => (
                            <FragmentCard
                                key={fragment.getUniqueIdentifier()}
                                fragment={fragment}
                                selectable={compilationMode}
                                onSelect={memorizedOnSelect}
                                selectedNumber={
                                    compilationSelection.findIndex(
                                        (item) => item === fragment.id,
                                    ) + 1
                                }
                                onDelete={memorizedOnDelete}
                                onEdit={memorizedOnEdit}
                                onUpdate={handleClipUpdate}
                            />
                        ))}
                </div>
            </LazyMotion>
            {searching && !searchPending && searchResults.length <= 0 && (
                <div className={styles.error}>
                    No Search results{" "}
                    {searchQuery &&
                        searchQuery !== "" &&
                        `for '${searchQuery}'`}
                </div>
            )}
            <div className={styles.scroll__push} ref={scrollPush}>
                <div className={styles.scroll__push__text}>
                    {(() => {
                        if (status === "error") {
                            return (
                                <div className={styles.error}>
                                    Error while getting clips
                                </div>
                            );
                        }
                        if (status === "loading") {
                            return (
                                <>
                                    Loading clips
                                    <LoadingDots />
                                </>
                            );
                        }
                        if (
                            status === "success" &&
                            clips.pages[0].length === 0
                        ) {
                            return (
                                <div className={styles.scroll__push__text}>
                                    There are no videos here yet,{" "}
                                    <NavLink
                                        className={styles.livestreamlink}
                                        to="/channels"
                                    >
                                        create a clip
                                    </NavLink>{" "}
                                    or{" "}
                                    <NavLink
                                        className={styles.livestreamlink}
                                        onClick={handleUploadClick}
                                        to="/clips"
                                    >
                                        upload
                                    </NavLink>{" "}
                                    <input
                                        type="file"
                                        accept="video/*"
                                        ref={hiddenFileInput}
                                        onChange={initiateUploads}
                                        style={{ display: "none" }}
                                        multiple
                                    />
                                    a local video to get started!
                                </div>
                            );
                        }
                        if (fragmentsEnded) {
                            return "No more clips available";
                        }
                        return null;
                    })()}
                </div>
            </div>
            {clips?.pages && clips.pages[0].length === 0 && <LoginOnboarding />}
            {clips?.pages && clips.pages[0].length > 0 && <ClipsOnboarding />}
        </div>
    );
};

export default Clips;
